import { useState } from 'react';
import { Line, Bar, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const DUMMY_DATA = {
  revenue: {
    labels: MONTHS,
    datasets: [
      {
        label: 'Revenue',
        data: [65000, 59000, 80000, 81000, 56000, 85000, 90000, 78000, 95000, 67000, 88000, 96000],
        borderColor: 'rgb(75, 192, 192)',
        tension: 0.1,
        fill: false,
      },
    ],
  },
  expenses: {
    labels: MONTHS,
    datasets: [
      {
        label: 'Expenses',
        data: [45000, 49000, 52000, 47000, 46000, 55000, 60000, 58000, 65000, 57000, 68000, 76000],
        backgroundColor: 'rgba(255, 99, 132, 0.5)',
      },
    ],
  },
  profitLoss: {
    labels: ['Profit', 'Loss', 'Operating Expenses'],
    datasets: [
      {
        data: [300000, 50000, 150000],
        backgroundColor: [
          'rgba(75, 192, 192, 0.5)',
          'rgba(255, 99, 132, 0.5)',
          'rgba(255, 206, 86, 0.5)',
        ],
      },
    ],
  },
};

export default function Reports() {
  const [dateRange, setDateRange] = useState('year');
  const [reportType, setReportType] = useState('all');

  return (
    <div className="container mx-auto px-4">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Reports</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          View and analyze your business performance
        </p>
      </div>

      <div className="mb-6 flex flex-col md:flex-row gap-4 justify-between items-start md:items-center">
        <div className="flex flex-wrap gap-4">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="block w-40 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          >
            <option value="year">This Year</option>
            <option value="quarter">This Quarter</option>
            <option value="month">This Month</option>
            <option value="custom">Custom Range</option>
          </select>

          <select
            value={reportType}
            onChange={(e) => setReportType(e.target.value)}
            className="block w-40 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          >
            <option value="all">All Reports</option>
            <option value="revenue">Revenue</option>
            <option value="expenses">Expenses</option>
            <option value="profit">Profit & Loss</option>
          </select>
        </div>

        <div className="flex gap-2">
          <button className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
            Download PDF
          </button>
          <button className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
            Export Excel
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Revenue Trend */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Revenue Trend</h3>
          <Line
            data={DUMMY_DATA.revenue}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  position: 'top',
                },
              },
              scales: {
                y: {
                  beginAtZero: true,
                  ticks: {
                    callback: (value) => '₹' + value.toLocaleString('en-IN'),
                  },
                },
              },
            }}
          />
        </div>

        {/* Expenses */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Monthly Expenses</h3>
          <Bar
            data={DUMMY_DATA.expenses}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  position: 'top',
                },
              },
              scales: {
                y: {
                  beginAtZero: true,
                  ticks: {
                    callback: (value) => '₹' + value.toLocaleString('en-IN'),
                  },
                },
              },
            }}
          />
        </div>

        {/* Profit & Loss */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Profit & Loss Overview</h3>
          <div className="aspect-w-2 aspect-h-1">
            <Pie
              data={DUMMY_DATA.profitLoss}
              options={{
                responsive: true,
                plugins: {
                  legend: {
                    position: 'right',
                  },
                },
              }}
            />
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Revenue</h3>
            <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
              ₹9,40,000
            </p>
            <p className="mt-2 text-sm text-green-600">
              <span className="font-medium">↑ 12%</span>
              <span className="ml-2">vs last year</span>
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Expenses</h3>
            <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
              ₹6,78,000
            </p>
            <p className="mt-2 text-sm text-red-600">
              <span className="font-medium">↑ 8%</span>
              <span className="ml-2">vs last year</span>
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Net Profit</h3>
            <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
              ₹2,62,000
            </p>
            <p className="mt-2 text-sm text-green-600">
              <span className="font-medium">↑ 15%</span>
              <span className="ml-2">vs last year</span>
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Profit Margin</h3>
            <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
              27.8%
            </p>
            <p className="mt-2 text-sm text-green-600">
              <span className="font-medium">↑ 2.5%</span>
              <span className="ml-2">vs last year</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
} 